import { useEffect, useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useItemKinds } from '@/features/inventory/hooks';
import {
  ancestorIds,
  bomTypesForItem,
  subAssemblyKindSlugs,
  useAddBomLines,
  type BomGraph,
} from '@/features/manufacturing/boms';
import { cn } from '@/lib/utils';
import type { BomAssemblyTarget } from './AddBomComponentsDialog';

interface CopyBomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: BomAssemblyTarget;
  graph: BomGraph | undefined;
}

/**
 * Copy every line of another BOM onto this one — the fast path for
 * variants (build Renew Manual White once, copy it onto Black / Grey /
 * EU / ASNZ, then swap the colour-specific parts). Lines already on the
 * target are left alone.
 */
export function CopyBomDialog({ open, onOpenChange, target, graph }: CopyBomDialogProps) {
  const addLines = useAddBomLines();
  const { data: kinds } = useItemKinds();
  const subAssemblyKinds = useMemo(() => subAssemblyKindSlugs(kinds), [kinds]);
  const [query, setQuery] = useState('');
  const [sourceId, setSourceId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSourceId(null);
    }
  }, [open]);

  const blocked = useMemo(() => {
    const set = ancestorIds(graph, target.itemId);
    set.add(target.itemId);
    return set;
  }, [graph, target.itemId]);

  const targetParentSku = target.parentSku ?? null;

  const sources = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (graph?.boms ?? [])
      .filter((b) => b.item_id !== target.itemId && b.lines.length > 0 && b.item)
      .filter(
        (b) =>
          !q || b.item!.sku.toLowerCase().includes(q) || b.item!.name.toLowerCase().includes(q),
      )
      .sort((a, b) => {
        const aSib = targetParentSku && a.item!.parent_sku === targetParentSku ? 0 : 1;
        const bSib = targetParentSku && b.item!.parent_sku === targetParentSku ? 0 : 1;
        return aSib - bSib || a.item!.sku.localeCompare(b.item!.sku);
      });
  }, [graph, query, target.itemId, targetParentSku]);

  const source = sourceId ? (graph?.byItemId.get(sourceId) ?? null) : null;
  const archivedSkipped = (source?.lines ?? []).filter((l) => l.item?.status === 'archived').length;
  const copyable = (source?.lines ?? []).filter(
    (l) => !blocked.has(l.component_item_id) && l.item?.status !== 'archived',
  );

  const handleCopy = async () => {
    if (!source) return;
    if (copyable.length === 0) {
      toast.error(
        archivedSkipped
          ? 'Every component on that BOM is archived, so nothing was copied.'
          : 'Nothing on that BOM can be copied onto this item.',
      );
      return;
    }
    try {
      const { added, skipped } = await addLines.mutateAsync({
        assemblyItemId: target.itemId,
        assemblyName: target.name,
        lines: copyable.map((l) => ({
          componentItemId: l.component_item_id,
          quantity: l.quantity,
          uom: l.uom,
          scrapPct: l.scrap_pct,
          notes: l.notes,
        })),
      });
      toast.success(
        `Copied ${added} line${added === 1 ? '' : 's'} from ${source.item?.sku}` +
          (skipped ? ` (${skipped} already on this BOM)` : '') +
          (archivedSkipped
            ? ` (${archivedSkipped} archived component${archivedSkipped === 1 ? '' : 's'} left off)`
            : ''),
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not copy the BOM.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Copy lines from another BOM</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search BOMs by SKU or name…"
            className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
        <div className="max-h-72 overflow-y-auto rounded-md border border-border">
          {sources.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              No other BOMs with lines yet.
            </div>
          ) : (
            sources.map((b) => {
              const selected = b.item_id === sourceId;
              const sibling = !!targetParentSku && b.item?.parent_sku === targetParentSku;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setSourceId(b.item_id)}
                  className={cn(
                    'flex w-full items-center gap-2 border-b border-border/60 px-3 py-1.5 text-left text-sm last:border-b-0 hover:bg-secondary/60',
                    selected && 'bg-secondary',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border',
                      selected ? 'border-primary bg-primary text-primary-foreground' : 'opacity-40',
                    )}
                  >
                    {selected && <Check className="h-3 w-3" />}
                  </span>
                  <span className="w-44 shrink-0 truncate font-mono text-xs text-muted-foreground">
                    {b.item?.sku}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{b.item?.name}</span>
                  {sibling && <Badge variant="info-soft">Same family</Badge>}
                  <Badge variant="muted">
                    {b.item && bomTypesForItem(b.item, graph, subAssemblyKinds).has('sub_assembly')
                      ? 'Sub-assy'
                      : 'FG'}
                  </Badge>
                  <span className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {b.lines.length} lines
                  </span>
                </button>
              );
            })
          )}
        </div>

        {source && copyable.length < source.lines.length && (
          <p className="text-xs text-muted-foreground">
            {source.lines.length - copyable.length} line(s) skipped because they would make this BOM
            circular.
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={addLines.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopy}
            disabled={!source || copyable.length === 0 || addLines.isPending}
          >
            {addLines.isPending
              ? 'Copying…'
              : source
                ? `Copy ${copyable.length} line${copyable.length === 1 ? '' : 's'}`
                : 'Copy lines'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
